import clsx from "clsx";

import Button from "./forms/button";
import { badgeColorClass } from "./badge-color";
import type { BadgeColor } from "./badge-color";

import badgeStyles from "./badge.module.css";
import styles from "./dismissible-chip.module.css";

export interface DismissibleChipProps {
  label: string;
  color: BadgeColor;
  /**
   * Present -> a trailing icon-only dismiss control renders inside the chip;
   * absent -> the chip is a plain badge with no X. Same optional-affordance
   * idiom as `Alert`'s `onDismiss` (SR-301 phase 7) — callers with a
   * sometimes-editable list (`zones.tsx`'s product-type chip only shows its
   * X while the row is in edit mode) pass `undefined` rather than
   * conditionally rendering two different components.
   */
  onDismiss?: () => void;
  /**
   * The dismiss button's accessible name. Required, and not defaulted from
   * `PalletLangContext` the way `Alert`'s and `Search`'s are: "Dismiss" /
   * "Clear search" are generic verbs that read fine with no object, but
   * "remove this chip" needs the caller's own noun ("Remove zone", "Remove
   * product type") to be useful to a screen reader user.
   */
  dismissLabel: string;
}

/**
 * A `Badge` with a built-in dismiss action — retires the two-widget "coloured
 * pill, plus a separate icon-only `Button`" rows `zones.tsx` and
 * `user-groups.tsx` hand-rolled (SR-301 phase 8 doc §4j, G-09). Shares
 * `Badge`'s geometry (`badge.module.css`'s `.badge`) rather than importing
 * `Badge` and wrapping it, so the dismiss button sits inside the same
 * coloured pill instead of beside a second one.
 */
function DismissibleChip({
  label,
  color,
  onDismiss,
  dismissLabel,
}: DismissibleChipProps) {
  return (
    <span
      className={clsx(badgeStyles.badge, badgeColorClass(color), {
        [styles.withDismiss]: onDismiss !== undefined,
      })}
      data-test-role="dismissible-chip"
    >
      {label}
      {onDismiss ? (
        <Button
          kind="icon-only"
          variant="bare"
          icon="X"
          label={dismissLabel}
          onPress={onDismiss}
        />
      ) : null}
    </span>
  );
}

export default DismissibleChip;
